// src/components/game/SortingGame.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SortingGame = ({ isDarkMode }) => {
  const [numbers, setNumbers] = useState([5, 3, 8, 1, 4, 7, 2, 6]);
  const [isSorted, setIsSorted] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [bestScore, setBestScore] = useState(() => {
    const saved = localStorage.getItem('sortingBestScore');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [size, setSize] = useState(8);
  const [hint, setHint] = useState(false);

  useEffect(() => {
    let timer;
    if (isPlaying && !isSorted) {
      timer = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [isPlaying, isSorted]);

  const generateNumbers = (count) => {
    const nums = [];
    for (let i = 1; i <= count; i++) {
      nums.push(i);
    }
    return nums;
  };

  const shuffleArray = () => {
    const newNumbers = generateNumbers(size);
    const shuffled = [...newNumbers];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setNumbers(shuffled);
    setIsSorted(false);
    setAttempts(0);
    setTime(0);
    setIsPlaying(true);
    setSelectedIndex(null);
    setHint(false);
  };

  // ✅ swapNumbers ფუნქცია - გამოყენებულია onClick-ში
  const handleNumberClick = (index) => {
    if (isSorted || !isPlaying) return;
    
    if (selectedIndex === null) {
      setSelectedIndex(index);
    } else if (selectedIndex === index) {
      setSelectedIndex(null);
    } else {
      // Swap selected with current
      const newNumbers = [...numbers];
      [newNumbers[selectedIndex], newNumbers[index]] = [newNumbers[index], newNumbers[selectedIndex]];
      setNumbers(newNumbers);
      setAttempts(attempts + 1);
      setSelectedIndex(null);
      
      const sorted = [...newNumbers].sort((a, b) => a - b);
      if (JSON.stringify(newNumbers) === JSON.stringify(sorted)) {
        setIsSorted(true);
        setIsPlaying(false);
        const score = attempts + 1;
        if (score < bestScore || bestScore === 0) {
          setBestScore(score);
          localStorage.setItem('sortingBestScore', score.toString());
        }
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const getColor = (num, index) => {
    if (isSorted) return 'linear-gradient(135deg, #4ade80, #22d3ee)';
    if (selectedIndex === index) return 'linear-gradient(135deg, #f59e0b, #f97316)';
    if (num === index + 1) return 'linear-gradient(135deg, #34d399, #6ee7b7)';
    return 'linear-gradient(135deg, #667eea, #764ba2)';
  };

  const handleSizeChange = (newSize) => {
    setSize(newSize);
    const newNumbers = generateNumbers(newSize);
    const shuffled = [...newNumbers];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    setNumbers(shuffled);
    setIsSorted(false);
    setAttempts(0);
    setTime(0);
    setIsPlaying(true);
    setSelectedIndex(null);
    setHint(false);
  };

  const toggleHint = () => {
    setHint(!hint);
  };

  const getHint = () => {
    const sorted = [...numbers].sort((a, b) => a - b);
    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] !== sorted[i]) {
        return `💡 მინიშნება: რიცხვი ${sorted[i]} უნდა იყოს ${i + 1}-ე პოზიციაზე`;
      }
    }
    return '✅ ყველაფერი სწორად არის!';
  };

  const pageStyle = {
    minHeight: '100vh',
    background: isDarkMode 
      ? 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)' 
      : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    padding: 'clamp(12px, 3vw, 20px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };

  const containerStyle = {
    maxWidth: 'min(700px, 95vw)',
    width: '100%',
    padding: 'clamp(15px, 4vw, 35px)',
    background: isDarkMode 
      ? 'rgba(26, 26, 46, 0.9)' 
      : 'rgba(255, 255, 255, 0.9)',
    borderRadius: 'clamp(16px, 2vw, 20px)',
    backdropFilter: 'blur(20px)',
    boxShadow: isDarkMode 
      ? '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.1)' 
      : '0 20px 60px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.5)',
    border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
    animation: 'slideUp 0.6s ease-out'
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(30px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }
          .game-title {
            background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-size: 200% auto;
            animation: shimmer 3s linear infinite;
          }
          .bounce {
            animation: bounce 0.5s ease infinite;
          }
        `}
      </style>

      <Link to="/games" style={{
        padding: 'clamp(10px, 1.5vw, 12px) clamp(20px, 3vw, 28px)',
        margin: 'clamp(8px, 1vw, 10px) 0',
        background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
        color: isDarkMode ? '#fff' : '#000',
        textDecoration: 'none',
        borderRadius: 'clamp(10px, 1.5vw, 12px)',
        backdropFilter: 'blur(10px)',
        border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
        transition: 'all 0.3s',
        fontSize: 'clamp(13px, 1.5vw, 15px)'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateX(-5px)';
        e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateX(0)';
        e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
      }}>
        ← თამაშებში დაბრუნება
      </Link>

      <div style={containerStyle}>
        <h1 style={{ 
          textAlign: 'center',
          fontSize: 'clamp(22px, 4vw, 36px)',
          marginBottom: '5px'
        }}>
          <span className="game-title">📊 სორტირების თამაში</span>
        </h1>
        <p style={{ 
          color: isDarkMode ? '#aaa' : '#666', 
          textAlign: 'center',
          fontSize: 'clamp(12px, 1.5vw, 16px)'
        }}>
          დაალაგე რიცხვები ზრდადობით!
        </p>

        {/* Size Selector */}
        <div style={{
          display: 'flex',
          gap: 'clamp(6px, 1vw, 8px)',
          justifyContent: 'center',
          margin: 'clamp(12px, 1.5vw, 15px) 0',
          flexWrap: 'wrap'
        }}>
          <span style={{ 
            color: isDarkMode ? '#aaa' : '#666',
            fontSize: 'clamp(12px, 1.2vw, 13px)',
            display: 'flex',
            alignItems: 'center'
          }}>
            📐 ზომა:
          </span>
          {[6, 8, 10, 12].map(s => (
            <button
              key={s}
              onClick={() => handleSizeChange(s)}
              style={{
                padding: 'clamp(4px, 0.8vw, 6px) clamp(12px, 1.5vw, 14px)',
                borderRadius: '20px',
                border: '2px solid',
                borderColor: size === s ? '#667eea' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                background: size === s ? '#667eea' : 'transparent',
                color: size === s ? '#fff' : (isDarkMode ? '#aaa' : '#666'),
                fontSize: 'clamp(12px, 1.2vw, 13px)',
                cursor: 'pointer',
                transition: 'all 0.3s',
                fontWeight: size === s ? 'bold' : 'normal',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              {s}x{s}
            </button>
          ))}
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(65px, 1fr))',
          gap: 'clamp(8px, 1vw, 10px)',
          margin: 'clamp(12px, 1.5vw, 15px) 0',
          padding: 'clamp(10px, 1.5vw, 15px)',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
          borderRadius: 'clamp(10px, 1.5vw, 12px)',
          textAlign: 'center'
        }}>
          <div>
            <div style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', color: '#FF6B6B' }}>🔄</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>{attempts}</div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 10px)', color: isDarkMode ? '#666' : '#999' }}>ცვლილება</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', color: '#4ECDC4' }}>⏱️</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>{formatTime(time)}</div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 10px)', color: isDarkMode ? '#666' : '#999' }}>დრო</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', color: '#FFD93D' }}>🏆</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>{bestScore || '--'}</div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 10px)', color: isDarkMode ? '#666' : '#999' }}>საუკეთესო</div>
          </div>
          <div>
            <div style={{ fontSize: 'clamp(18px, 2.5vw, 20px)', color: '#a78bfa' }}>📦</div>
            <div style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: isDarkMode ? '#fff' : '#000' }}>{numbers.length}</div>
            <div style={{ fontSize: 'clamp(8px, 0.8vw, 10px)', color: isDarkMode ? '#666' : '#999' }}>ელემენტი</div>
          </div>
        </div>

        {/* Numbers Grid */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'clamp(6px, 1.5vw, 12px)',
          justifyContent: 'center',
          margin: 'clamp(15px, 2vw, 20px) 0',
          padding: 'clamp(12px, 2vw, 20px)',
          background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.02)',
          borderRadius: 'clamp(14px, 1.5vw, 16px)',
          border: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)',
          minHeight: 'clamp(100px, 15vw, 120px)'
        }}>
          {numbers.map((num, index) => {
            const isCorrectPosition = num === index + 1;
            return (
              <div
                key={index}
                onClick={() => handleNumberClick(index)}
                style={{
                  width: 'clamp(45px, 8vw, 70px)',
                  height: 'clamp(45px, 8vw, 70px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(18px, 3vw, 28px)',
                  fontWeight: 'bold',
                  borderRadius: 'clamp(12px, 1.5vw, 14px)',
                  cursor: isSorted || !isPlaying ? 'default' : 'pointer',
                  background: getColor(num, index),
                  color: '#fff',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isSorted ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isSorted 
                    ? '0 4px 20px rgba(74,222,128,0.4)' 
                    : isCorrectPosition && !isSorted
                      ? '0 4px 15px rgba(52,211,153,0.3)' 
                      : '0 4px 15px rgba(102,126,234,0.2)',
                  animation: isSorted ? 'pulse 1s infinite' : 'none',
                  border: isSorted ? 'none' : (isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)'),
                  position: 'relative',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
              >
                {num}
                {isCorrectPosition && !isSorted && (
                  <span style={{
                    position: 'absolute',
                    top: '-6px',
                    right: '-6px',
                    fontSize: '12px',
                    background: 'rgba(52,211,153,0.8)',
                    borderRadius: '50%',
                    padding: '2px',
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    ✅
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Hint */}
        {hint && !isSorted && (
          <div style={{
            padding: 'clamp(10px, 1.5vw, 12px) clamp(14px, 2vw, 16px)',
            marginBottom: 'clamp(12px, 1.5vw, 15px)',
            background: isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.03)',
            borderRadius: 'clamp(10px, 1.5vw, 12px)',
            border: '1px solid #f59e0b',
            color: isDarkMode ? '#fff' : '#000',
            fontSize: 'clamp(13px, 1.5vw, 14px)',
            textAlign: 'center'
          }}>
            {getHint()}
          </div>
        )}

        {isSorted && (
          <div style={{
            padding: 'clamp(16px, 2vw, 20px)',
            background: 'linear-gradient(135deg, #4ade80, #22d3ee)',
            borderRadius: 'clamp(10px, 1.5vw, 12px)',
            color: '#fff',
            textAlign: 'center',
            marginBottom: 'clamp(15px, 2vw, 20px)',
            animation: 'pulse 1.5s infinite',
            fontWeight: 'bold',
            fontSize: 'clamp(14px, 2vw, 20px)',
            boxShadow: '0 4px 30px rgba(74,222,128,0.3)'
          }}>
            🎉 გილოცავთ! რიცხვები სწორად არის! 🎉
            <br />
            <span style={{ fontSize: 'clamp(12px, 1.5vw, 14px)', opacity: 0.9 }}>
              {attempts} ცვლილება • {formatTime(time)} წამი
            </span>
            <br />
            <span style={{ fontSize: 'clamp(12px, 1.5vw, 14px)', opacity: 0.8 }}>
              🏆 საუკეთესო: {bestScore} ცვლილება
            </span>
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: 'clamp(8px, 1vw, 10px)',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={shuffleArray}
            style={{
              flex: 1,
              padding: 'clamp(14px, 1.5vw, 16px)',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              fontSize: 'clamp(14px, 1.5vw, 18px)',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              boxShadow: '0 4px 20px rgba(245,87,108,0.3)',
              letterSpacing: '0.5px',
              touchAction: 'manipulation',
              minWidth: '80px'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px) scale(1.02)';
              e.target.style.boxShadow = '0 8px 30px rgba(245,87,108,0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0) scale(1)';
              e.target.style.boxShadow = '0 4px 20px rgba(245,87,108,0.3)';
            }}
          >
            🔀 ახალი თამაში
          </button>
          <button
            onClick={() => setSelectedIndex(null)}
            style={{
              padding: 'clamp(14px, 1.5vw, 16px) clamp(16px, 2vw, 20px)',
              background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
              color: isDarkMode ? '#fff' : '#000',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.05)',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              fontSize: 'clamp(13px, 1.5vw, 16px)',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              touchAction: 'manipulation'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
            }}
          >
            🧹 გაუქმება
          </button>
          <button
            onClick={toggleHint}
            style={{
              padding: 'clamp(14px, 1.5vw, 16px) clamp(16px, 2vw, 20px)',
              background: hint ? '#f59e0b' : (isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'),
              color: hint ? '#fff' : (isDarkMode ? '#fff' : '#000'),
              border: 'none',
              borderRadius: 'clamp(10px, 1.5vw, 12px)',
              fontSize: 'clamp(13px, 1.5vw, 16px)',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              boxShadow: hint ? '0 4px 20px rgba(245,158,11,0.3)' : 'none',
              touchAction: 'manipulation'
            }}
            onMouseEnter={(e) => {
              if (!hint) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!hint) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.background = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
              }
            }}
          >
            {hint ? '🙈 მინიშნება' : '💡 მინიშნება'}
          </button>
        </div>

        <div style={{
          marginTop: 'clamp(12px, 1.5vw, 15px)',
          padding: 'clamp(10px, 1vw, 12px)',
          textAlign: 'center',
          fontSize: 'clamp(10px, 1vw, 12px)',
          color: isDarkMode ? '#666' : '#999',
          borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)'
        }}>
          💡 დააკლიკე ორ რიცხვს მათი ადგილების გასაცვლელად
          <br />
          <span style={{ fontSize: 'clamp(9px, 0.8vw, 10px)', opacity: 0.7 }}>
            🟢 მწვანე - სწორ პოზიციაზეა
          </span>
        </div>
      </div>
    </div>
  );
};

export default SortingGame;